/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */
import { html, LitElement } from 'lit-element';
import { sharedStyles } from '../../styles/shared-styles.js';
import { dispatchAttributeChangedEvent } from '../../utils/attribute-changed-event-dispatcher.js';
import { KEYCODE } from '../../utils/keycode.js';
import {
  AmPmValues,
  MinuteValues,
  SecondValues,
  TwelveHourValues,
  TwentyFourHourValues,
} from '../../utils/time-value-generator.js';
import { wait } from '../../utils/wait.js';
import './sh-timepicker-input.js';
import './sh-timepicker-panel.js';
import { timepickerStyles } from './sh-timepicker-styles.js';
import { TimeInputStateHandlerMixin } from './time-input-state-handler-mixin.js';
import { TimeInputValidatorMixin } from './time-input-validator-mixin.js';
import { TimeModifierMixin } from './time-modifier-mixin.js';

/**
 * @prop {String} label - Default Time. Defines the label of the Timepicker
 * @prop {String} h - Defines hours field of the Timepicker
 * @prop {String} m - Defines minutes field of the Timepicker
 * @prop {String} s - Defines seconds field of the Timepicker
 * @prop {String} amPm - Defines am/pm field of the Timepicker
 * @attr {String} am-pm - Defines am/pm field of the Timepicker
 * @prop {Boolean} mini - Default false. Defines alternate format HH:MM AM/PM for Timepicker
 * @prop {String} step - Default 1. Defines increment/decrement step for hour and minute selector (5,10,15,30)
 * @prop {Boolean} error - If set to `true`, the input text displays styles for invalid input
 * @prop {Boolean} success - If set to `true`, the input text displays styles for valid input
 * @prop {String} errorMessage - Defines the message shown when error is set to true
 * @attr {String} error-message - Defines the message shown when error is set to true
 * @prop {Boolean} safety - Default false. If set to `true`, the input text displays styles for safety-relevant input (yellow underline)
 * @prop {Boolean} mandatory - If set to `true`, the input text displays styles for mandatory input
 * @prop {Boolean} noBorder - Default false. If set to `true`, the bottom border will not be displayed
 * @attr {Boolean} no-border - Default false. If set to true, the bottom border will not be displayed
 * @prop {Boolean} responsive - When set to true, the timepicker is displayed within a drawer placed at the bottom of the screen. It is applicable for mobile devices.
 * @prop {Boolean} condensed - If time picker is inside a table, then the condensed format of time picker is used to display information. This feature cannot be overriden.
 * @prop {Boolean} disabled -  If set, won't allow mouse events and disabled styles will be applied to the timepicker input.
 * @prop {String} clockFormat - Defines whether time mode is 12-hour / 24-hour . Possible values are `"12-hour"` or `"24-hour"`
 * @attr {String} clock-format - Defines whether time mode is 12-hour / 24-hour . Possible values are `"12-hour"` or `"24-hour"`
 * @prop {String} val - Displays the time value "hh:mm:ss"
 * @prop {Boolean} timeInputState - *hide
 * @prop {Boolean} active - *hide
 */
export class SHTimepicker extends TimeInputValidatorMixin(
  TimeInputStateHandlerMixin(TimeModifierMixin(LitElement))
) {
  static get properties() {
    return {
      active: { type: Boolean, reflect: true },
      /** @ignore */
      mini: { type: Boolean, reflect: true },
      step: { type: String, reflect: true },
      responsive: { type: Boolean, reflect: true },
      label: { type: String, reflect: true },
      disabled: { type: Boolean, reflect: true },
      h: { type: String, reflect: true },
      m: { type: String, reflect: true },
      s: { type: String, reflect: true },
      /** @ignore */
      amPm: { type: String, reflect: true, attribute: 'am-pm' },
      /** @ignore */
      val: { type: String, reflect: true },
      clockFormat: { type: String, reflect: true, attribute: 'clock-format' },
      error: { type: Boolean, reflect: true },
      /** @ignore */
      errorMessage: { type: String, reflect: true, attribute: 'error-message' },
      /** @ignore */
      hint: { type: String, reflect: true },
      success: { type: Boolean, reflect: true },
      safety: { type: Boolean, reflect: true },
      /** @ignore */
      noBorder: { type: Boolean, reflect: true },
      mandatory: { type: Boolean, reflect: true },
      /** @ignore */
      open: { type: Boolean, reflect: true },
      /** @ignore */
      close: { type: Boolean, reflect: true },
      condensed: { type: Boolean, reflect: true },
      _showDrawerDOM: { type: Boolean, reflect: true },
    };
  }
  static get styles() {
    return [timepickerStyles, sharedStyles];
  }
  render() {
    return html`
    <div class="timepicker-wrapper" id="timepickerWrapper">
          <div class="timepicker-input"  @click=${this._makeMobileTimepickerActive}>
            <div class="label-area">
              <div class="label-slot"><div class="label">${this.label}</div>
              ${this.mandatory ? html` <span id="mandatoryStarSymbol">*</span>` : ''}
              </div>
                <div class="input-slot">
                  <sh-timepicker-input id="time-input-field"
                  @time-value-changed = ${(e) => this._updateTimeBasedOnInputOrPanelValue(e)}
                  .h="${this.h}"
                  .m="${this.m}"
                  .s="${this.s}"
                  .amPm=${this.amPm}
                  .step =${this.step}
                  .mini=${this.mini}
                  ?disabled=${this.disabled}
                  clock-format="${this.clockFormat}" ></sh-timepicker-input>
                </div> 
              </div> 
              <sh-icon button tabindex="0" @click=${this._clearTimeInputField} 
              size="s" icon="cancel" class="close-button" id="closeIcon" ></sh-icon>
          <div class="icon-wrapper">
            ${
              this.error || this.success
                ? html` <sh-icon
                    class="error-success-icon"
                    icon=${this.error ? 'error' : 'success'}
                    size="s"
                    ?disabled="${this.disabled}"
                  ></sh-icon>`
                : ''
            }
          </div>
            <div class="schedule-button"> 
              <sh-icon button tabindex= ${this.disabled ? -1 : 0}  @click=${
      this._makeTimepickerActive
    } size="s" icon="scheduled" 
    }" id="scheduledIcon"></sh-icon>
            </div>
          </div>
          ${
            this.responsive
              ? this._showDrawerDOM
                ? this._timepickerMobileTemplate()
                : ''
              : html`
                  <sh-overlay
                    class="overlay"
                    style="width:${getComputedStyle(this).width}; height: 136px; "
                    scroll-action="cancel"
                    no-overlap
                    horizontal-align="left"
                  >
                    <sh-timepicker-panel
                      id="time-panel"
                      @time-value-changed=${(e) => this._updateTimeBasedOnInputOrPanelValue(e)}
                      .h="${this.h}"
                      .m="${this.m}"
                      .s="${this.s}"
                      .amPm=${this.amPm}
                      .step=${this.step}
                      .mini=${this.mini}
                      active=${this.active}
                      clock-format="${this.clockFormat}"
                    ></sh-timepicker-panel>
                  </sh-overlay>
                `
          } 
    </div>
        ${
          this.hint
            ? html` <sh-text size="body-2" class="helper-text hint" color="secondary"
                >${this.hint}</sh-text
              >`
            : ''
        }
        ${
          this.error && this.errorMessage
            ? html` <sh-text size="body-2" class="helper-text error-message" color="secondary"
                >${this.errorMessage}</sh-text
              >`
            : ''
        }
    `;
  }
  constructor() {
    super();
    this.h = this.m = this.s = this.amPm = '';
    this.clockFormat = '12-hour';
    this.label = 'Time';
    this.mini = false;
    this.step = 1;
    this.error = this.safety = this.mandatory = this.noBorder = false;
  }
  connectedCallback() {
    super.connectedCallback();
    this._updateInputFieldStateBasedOnInputValue();
    if (!this.hasAttribute('tabindex')) {
      this.setAttribute('tabindex', 0);
    }
    this.addEventListener(
      'keyup',
      this._toggleSubMenuIfPressedOnEnterAnywhereExceptScheduleIcon.bind(this)
    );
  }
  updated(changedProperties) {
    if (
      changedProperties.has('h') ||
      changedProperties.has('m') ||
      changedProperties.has('s') ||
      changedProperties.has('amPm')
    ) {
      this._updateTimeInValue();
    }
    if (changedProperties.has('active')) {
      this._handleActiveChanged();
    }
    if (changedProperties.has('disabled')) {
      this.tabIndex = this.disabled ? -1 : 0;
    }
    const listOfProperties = ['mini', 'condensed', 'h', 'm', 's', 'amPm', 'step', 'clockFormat'];
    dispatchAttributeChangedEvent(this, changedProperties, listOfProperties);
  }
  _makeTimepickerActive() {
    if (!this.responsive) {
      this.active = !this.active;
    }
  }
  _updateTimeBasedOnInputOrPanelValue(e) {
    this.h = e.target.h;
    this.m = e.target.m;
    this.s = e.target.s;
    this.amPm = e.target.amPm;
    this.step = e.target.step;
    this.clockFormat = e.target.clockFormat;
    if (this.h > 0 || this.m > 0 || this.s > 0 || this.amPm > 0) {
      this.classList.remove('noValue');
      this.classList.remove('hasCursor');
      this.classList.add('hasValue');
    }
  }
  _toggleSubMenuIfPressedOnEnterAnywhereExceptScheduleIcon(e) {
    let enteredOnScheduleIcon = false;
    const scheduleIcon = this.shadowRoot.querySelector('#scheduledIcon');
    const closeIcon = this.shadowRoot.querySelector('#closeIcon');
    const keyupComposedPath = e.composedPath();
    for (let i = 0; i < keyupComposedPath.length; i++) {
      if (keyupComposedPath[i] === scheduleIcon || keyupComposedPath[i] === closeIcon) {
        enteredOnScheduleIcon = true;
        break;
      }
    }
    if (e.keyCode === KEYCODE.ENTER && !enteredOnScheduleIcon) {
      scheduleIcon.click();
    }
  }
  _updateTimeInValue() {
    this.validateTime();
    this.val = `${this.h}:${this.m}:${this.s}${
      this.clockFormat === '12-hour' ? ':' + this.amPm : ''
    }`;
  }
  _handleOverlay() {
    if (this.active && !this.responsive) {
      const overlay = this.shadowRoot.querySelector('.overlay');
      overlay.open();
    }
  }
  _clearTimeInputField(e) {
    e.stopPropagation();
    if (e.currentTarget === this.shadowRoot.querySelector('.close-button')) {
      this.h = this.m = this.s = this.amPm = '';
    }
    this._handleOverlay();
  }
  _handleActiveChanged() {
    if (this.responsive) {
      this.active ? this._openMobileDrawer() : this._closeMobileDrawer();
    } else {
      const overlay = this.shadowRoot.querySelector('.overlay');
      this.active ? overlay.open() : overlay.close();
    }
  }

  /*mobile*/
  _timepickerMobileTemplate() {
    return html`<sh-scrollpicker
      @values-changed="${(e) => {
        this._updateValueFromScrollPicker(e.target);
      }}"
      @visible-changed="${(e) => {
        if (!e.target.visible) {
          this.active = false;
        }
      }}"
    ></sh-scrollpicker>`;
  }
  _makeMobileTimepickerActive() {
    if (this.responsive) {
      this.active = !this.active;
    } else {
      this._handleOverlay();
    }
  }
  async _openMobileDrawer() {
    const beforeDrawerOpensWaitTime = 50;
    this._showDrawerDOM = true;
    await wait(beforeDrawerOpensWaitTime);
    this._initialiseScrollpicker();
  }
  /* The basic initialisation code */
  async _initialiseScrollpicker() {
    const scrollPicker = this.shadowRoot.querySelector('sh-scrollpicker');
    let hourValues = [];
    if (this.clockFormat === '12-hour') {
      hourValues = TwelveHourValues();
    } else {
      hourValues = TwentyFourHourValues();
    }
    let minuteValues = MinuteValues();
    let secValues = SecondValues();
    let amPmValues = AmPmValues();
    /**
     * here is where you initialise the scrollpicker
     * for example if you want to change width of each section,
     * or alignment of each section, etc.
     */
    if (this.clockFormat === '12-hour') {
      if (!this.mini) {
        scrollPicker.items = [hourValues, minuteValues, secValues, amPmValues];
      } else {
        scrollPicker.items = [hourValues, minuteValues, amPmValues];
      }
    } else {
      if (!this.mini) {
        scrollPicker.items = [hourValues, minuteValues, secValues];
      } else {
        scrollPicker.items = [hourValues, minuteValues];
      }
    }
    scrollPicker.visible = true;
    if (
      !this.h ||
      !this.m ||
      (!this.s && !this.mini) ||
      (!this.amPm && this.clockFormat === '12-hour')
    ) {
      this.updateWithSystemTime();
      let selectedTimeValues = this._getSelectedValuesArray();
      scrollPicker.scrollToValues(selectedTimeValues);
    } else {
      let selectedTimeValues = this._getSelectedValuesArray();
      scrollPicker.scrollToValues(selectedTimeValues);
    }
  }
  _getSelectedValuesArray() {
    let valuesArray =
      this.mini && this.clockFormat === '12-hour'
        ? [this.h.toString(), this.m.toString(), this.amPm]
        : !this.mini && this.clockFormat === '24-hour'
        ? [this.h.toString(), this.m.toString(), this.s.toString()]
        : this.mini && this.clockFormat === '24-hour'
        ? [this.h.toString(), this.m.toString()]
        : !this.mini && this.clockFormat === '12-hour'
        ? [this.h.toString(), this.m.toString(), this.s.toString(), this.amPm]
        : '';

    return valuesArray;
  }
  _updateValueFromScrollPicker(scrollpicker) {
    if (!this.mini) {
      this.s = scrollpicker.values[2];
    }
    this.m = scrollpicker.values[1];
    this.h = scrollpicker.values[0];
    if (this.clockFormat === '12-hour') {
      this.amPm = this.mini ? scrollpicker.values[2] : scrollpicker.values[3];
    }
  }
  _closeMobileDrawer() {
    this._showDrawerDOM = false;
  }
}
customElements.define('sh-timepicker', SHTimepicker);
